"use client";

import React from "react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft01Icon, Calendar02Icon, ShieldIcon } from "@hugeicons/core-free-icons";
import Link from "next/link";

export interface LegalSection {
  title: string;
  content: string | React.ReactNode;
}

interface LegalPageTemplateProps {
  title: string;
  lastUpdated: string;
  description: string;
  sections: LegalSection[];
}

export default function LegalPageTemplate({
  title,
  lastUpdated,
  description,
  sections,
}: LegalPageTemplateProps) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 22,
      },
    },
  } as const;

  return (
    <div className="bg-neutral-50 dark:bg-black text-slate-900 dark:text-white transition-colors duration-700 ease-in-out relative min-h-screen w-full font-outfit">
      {/* Background ambient accents */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-blue-500/5 dark:bg-blue-950/5 blur-[180px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[650px] h-[650px] rounded-full bg-indigo-500/5 dark:bg-indigo-950/5 blur-[165px]" />
      </div>

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-28 md:py-36">
        {/* Back Link */}
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-500 dark:text-neutral-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300 mb-8 group outline-none"
        >
          <HugeiconsIcon
            icon={ArrowLeft01Icon}
            className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform duration-300"
            size={16}
          />
          Back to Home
        </Link>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header Section */}
          <motion.div variants={itemVariants} className="space-y-4">
            <div className="flex items-center gap-2.5 text-xs text-blue-600 dark:text-blue-400 font-bold tracking-wider uppercase">
              <HugeiconsIcon icon={ShieldIcon} className="w-4 h-4" size={16} />
              Legal & Policy
            </div>
            
            <h1 className="text-4xl md:text-5xl font-extrabold font-recoleta bg-gradient-to-r from-slate-950 via-slate-800 to-neutral-700 dark:from-white dark:via-neutral-100 dark:to-neutral-400 bg-clip-text text-transparent pb-1">
              {title}
            </h1>

            <div className="flex items-center gap-2 text-xs text-neutral-450 dark:text-neutral-500 font-medium">
              <HugeiconsIcon icon={Calendar02Icon} className="w-3.5 h-3.5" size={14} />
              Last Updated: {lastUpdated}
            </div>

            <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed font-manjari font-light max-w-3xl">
              {description}
            </p>
          </motion.div>

          <hr className="border-neutral-200/50 dark:border-neutral-900/50 my-8" />

          {/* Content Sections */}
          <motion.div variants={itemVariants} className="space-y-12">
            {sections.map((section, idx) => (
              <div key={idx} className="space-y-4 scroll-mt-24" id={`sec-${idx}`}>
                <h2 className="text-lg md:text-xl font-bold font-recoleta text-slate-850 dark:text-slate-100 flex items-center gap-3">
                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-neutral-150/50 dark:bg-neutral-900/50 border border-neutral-200/20 dark:border-neutral-800/20 text-neutral-500 dark:text-neutral-400">
                    {(idx + 1).toString().padStart(2, "0")}
                  </span>
                  {section.title}
                </h2>
                <div className="text-sm md:text-base text-neutral-650 dark:text-neutral-400 leading-relaxed font-manjari font-light space-y-4">
                  {typeof section.content === "string" ? (
                    <p>{section.content}</p>
                  ) : (
                    section.content
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
}
